class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.closeTimer = null;
    this.positionHandler = this.setFlyoutPosition.bind(this);
    this.bindPanelEvents();
    this.initializeHeaderCartLinks();
  }

  isDesktopFlyout() {
    return window.matchMedia('(min-width: 750px) and (hover: hover) and (pointer: fine)').matches;
  }

  bindPanelEvents() {
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay && overlay.dataset.cartDrawerBound !== 'true') {
      overlay.dataset.cartDrawerBound = 'true';
      overlay.addEventListener('click', () => this.close());
    }

    const panel = this.querySelector('.drawer__inner');
    if (panel && panel.dataset.cartDrawerHoverBound !== 'true') {
      panel.dataset.cartDrawerHoverBound = 'true';
      panel.addEventListener('mouseenter', () => this.cancelScheduledClose());
      panel.addEventListener('mouseleave', () => this.scheduleClose());
    }
  }

  cancelScheduledClose() {
    if (!this.closeTimer) return;
    window.clearTimeout(this.closeTimer);
    this.closeTimer = null;
  }

  scheduleClose() {
    if (!this.isDesktopFlyout()) return;
    this.cancelScheduledClose();
    this.closeTimer = window.setTimeout(() => this.close(), 550);
  }

  setFlyoutPosition() {
    const visibleCartLink = [...document.querySelectorAll('[data-native-cart-link]')].find(
      (link) => {
        const rect = link.getBoundingClientRect();
        return link.getClientRects().length > 0 && rect.bottom > 0 && rect.top < window.innerHeight;
      },
    );
    const triggerBottom = Math.max(
      0,
      visibleCartLink?.getBoundingClientRect().bottom ||
        document.querySelector('.section-header')?.getBoundingClientRect().bottom || 0,
    );
    const containerOffset = Math.max(1.6, (window.innerWidth - 1440) / 20 + 1.6);
    this.style.setProperty('--cart-flyout-top', `${triggerBottom}px`);
    this.style.setProperty('--cart-flyout-right', `${containerOffset}rem`);
  }

  initializeHeaderCartLinks() {
    const bindLinks = () => this.setHeaderCartIconAccessibility();

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', bindLinks, { once: true });
    } else {
      bindLinks();
    }

    document.addEventListener('shopify:section:load', bindLinks);
  }

  setHeaderCartIconAccessibility() {
    document.querySelectorAll('[data-native-cart-link]').forEach((cartLink) => {
      if (cartLink.dataset.cartDrawerBound === 'true') return;
      cartLink.dataset.cartDrawerBound = 'true';
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.setAttribute('aria-expanded', 'false');
      cartLink.addEventListener('click', () => this.close());
      cartLink.addEventListener('mouseenter', () => {
        if (!this.isDesktopFlyout()) return;
        this.open(cartLink);
      });
      cartLink.addEventListener('mouseleave', () => this.scheduleClose());
      cartLink.addEventListener('focus', () => {
        if (!this.isDesktopFlyout()) return;
        this.open(cartLink);
      });
    });
  }

  open(triggeredBy) {
    this.cancelScheduledClose();
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.setFlyoutPosition();
    this.bindPanelEvents();
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    if (this.classList.contains('active')) return;

    setTimeout(() => {
      this.classList.add('animate', 'active');
      document.querySelectorAll('[data-native-cart-link]').forEach((link) => link.setAttribute('aria-expanded', 'true'));
      if (!this.isDesktopFlyout()) {
        const panel = this.querySelector('.drawer__inner');
        panel.setAttribute('aria-modal', 'true');
        document.body.classList.add('overflow-hidden');
        trapFocus(panel, panel.querySelector('.drawer__close') || panel);
      }
    });

    window.addEventListener('resize', this.positionHandler);
    window.addEventListener('scroll', this.positionHandler, { passive: true });

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.cancelScheduledClose();
    this.classList.remove('active');
    const panel = this.querySelector('.drawer__inner');
    if (panel?.getAttribute('aria-modal') === 'true') {
      panel.setAttribute('aria-modal', 'false');
      document.body.classList.remove('overflow-hidden');
      removeTrapFocus(this.activeElement);
    }
    document.querySelectorAll('[data-native-cart-link]').forEach((link) => link.setAttribute('aria-expanded', 'false'));
    window.removeEventListener('resize', this.positionHandler);
    window.removeEventListener('scroll', this.positionHandler);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.bindPanelEvents();
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
